import type { BillingSummary, CloudUser } from '../app/cloud';
import { cloud } from '../app/cloud';
import { el } from './dom';

export interface AccountDialogOptions {
  user: CloudUser;
  billing: BillingSummary;
  onUser(user: CloudUser): void;
  onSignOut(): void;
}

const avatarData = async (file: File): Promise<string> => {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file');
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = 160;
  canvas.height = 160;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare that image');
  const scale = Math.max(canvas.width / bitmap.width, canvas.height / bitmap.height);
  const width = bitmap.width * scale;
  const height = bitmap.height * scale;
  context.drawImage(
    bitmap,
    (canvas.width - width) / 2,
    (canvas.height - height) / 2,
    width,
    height,
  );
  bitmap.close();
  return canvas.toDataURL('image/webp', 0.86);
};

/** Self-only profile, security, and subscription administration. */
export function openAccountDialog(options: AccountDialogOptions): () => void {
  document.querySelector('.account-overlay')?.remove();
  let user = options.user;
  let avatar = user.image ?? '';
  let busy = false;

  const panel = el('section', {
    class: 'account-dialog',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': 'account-title',
  });
  const overlay = el('div', { class: 'account-overlay' }, [panel]);
  const close = () => overlay.remove();
  overlay.onpointerdown = (event) => {
    if (event.target === overlay && !busy) close();
  };

  const render = (): void => {
    const message = el('p', { class: 'account-message', role: 'status' });
    const handle = el('input', {
      class: 'account-input',
      value: user.handle ?? '',
      autocomplete: 'username',
      minlength: 3,
      maxlength: 24,
      pattern: '[a-zA-Z][a-zA-Z0-9_-]{2,23}',
    }) as HTMLInputElement;
    const avatarPreview = avatar
      ? el('img', { class: 'account-avatar-preview', src: avatar, alt: '' })
      : el('span', { class: 'account-avatar-preview fallback' }, [
          (user.handle ?? 'K').slice(0, 1).toUpperCase(),
        ]);
    const file = el('input', {
      type: 'file',
      accept: 'image/png,image/jpeg,image/webp',
      hidden: true,
    }) as HTMLInputElement;
    file.onchange = () => {
      const selected = file.files?.[0];
      if (!selected) return;
      void avatarData(selected)
        .then((data) => {
          avatar = data;
          render();
        })
        .catch((error) => {
          message.textContent = (error as Error).message;
        });
    };

    const saveProfile = el(
      'button',
      {
        class: 'library-primary',
        type: 'button',
        onclick: () =>
          void (async () => {
            try {
              busy = true;
              saveProfile.textContent = 'Saving…';
              user = await cloud.updateAccount({ handle: handle.value, image: avatar || null });
              options.onUser(user);
              message.textContent = 'Profile saved';
            } catch (error) {
              message.textContent = (error as Error).message;
            } finally {
              busy = false;
              saveProfile.textContent = 'Save profile';
            }
          })(),
      },
      ['Save profile'],
    );

    const profile = el('section', { class: 'account-section' }, [
      el('h2', {}, ['Public profile']),
      el('p', {}, ['Your handle and avatar are visible to collaborators. Your email is private.']),
      el('div', { class: 'account-avatar-row' }, [
        avatarPreview,
        el('div', { class: 'account-avatar-actions' }, [
          el('button', { class: 'library-secondary', onclick: () => file.click() }, [
            'Choose image',
          ]),
          avatar
            ? el(
                'button',
                {
                  class: 'library-text-button',
                  onclick: () => {
                    avatar = '';
                    render();
                  },
                },
                ['Remove'],
              )
            : el('span'),
          file,
        ]),
      ]),
      el('label', { class: 'account-label' }, ['Public handle', handle]),
      saveProfile,
    ]);

    const securityChildren: HTMLElement[] = [
      el('h2', {}, ['Sign-in & security']),
      el('p', {}, [
        user.hasPassword
          ? 'Change your credential account. Email changes require verification.'
          : 'Sign-in is managed by Google. KilnCat does not store a password for this account.',
      ]),
    ];
    if (user.hasPassword) {
      const email = el('input', {
        class: 'account-input',
        type: 'email',
        value: user.email,
      }) as HTMLInputElement;
      const currentPassword = el('input', {
        class: 'account-input',
        type: 'password',
        placeholder: 'Current password',
      }) as HTMLInputElement;
      const newPassword = el('input', {
        class: 'account-input',
        type: 'password',
        placeholder: 'New password (8+ characters)',
        minlength: 8,
      }) as HTMLInputElement;
      securityChildren.push(
        el('div', { class: 'account-security-grid' }, [
          el('label', { class: 'account-label' }, ['Email', email]),
          el(
            'button',
            {
              class: 'library-secondary',
              onclick: () =>
                void cloud
                  .changeEmail(email.value.trim())
                  .then(
                    () => (message.textContent = 'Check your new address to confirm the change.'),
                  )
                  .catch((error) => (message.textContent = (error as Error).message)),
            },
            ['Change email'],
          ),
          el('label', { class: 'account-label' }, ['Current password', currentPassword]),
          el('label', { class: 'account-label' }, ['New password', newPassword]),
          el(
            'button',
            {
              class: 'library-secondary',
              onclick: () =>
                void cloud
                  .changePassword(currentPassword.value, newPassword.value)
                  .then(() => {
                    currentPassword.value = '';
                    newPassword.value = '';
                    message.textContent = 'Password changed';
                  })
                  .catch((error) => (message.textContent = (error as Error).message)),
            },
            ['Change password'],
          ),
        ]),
      );
    }
    if (!user.emailVerified) {
      securityChildren.push(
        el(
          'button',
          {
            class: 'library-text-button verify-email',
            onclick: () =>
              void cloud
                .sendVerificationEmail(user.email)
                .then(() => (message.textContent = 'Verification email requested.'))
                .catch((error) => (message.textContent = (error as Error).message)),
          },
          ['Resend verification email'],
        ),
      );
    }

    const billingAction = options.billing.customer
      ? el(
          'button',
          {
            class: 'library-secondary',
            onclick: () => void cloud.portal().then((url) => window.location.assign(url)),
          },
          ['Manage subscription'],
        )
      : options.billing.enabled
        ? el(
            'button',
            {
              class: 'library-primary',
              onclick: () => void cloud.checkout().then((url) => window.location.assign(url)),
            },
            ['Choose plan'],
          )
        : el('span', { class: 'account-muted' }, ['Billing is not enabled for this deployment.']);

    panel.replaceChildren(
      el('header', { class: 'account-dialog-header' }, [
        el('div', {}, [
          el('span', { class: 'welcome-kicker' }, ['Account']),
          el('h1', { id: 'account-title' }, [`@${user.handle ?? 'profile'}`]),
        ]),
        el('button', { class: 'account-close', 'aria-label': 'Close', onclick: close }, ['×']),
      ]),
      message,
      profile,
      el('section', { class: 'account-section' }, securityChildren),
      el('section', { class: 'account-section' }, [
        el('h2', {}, ['Billing']),
        el('p', {}, [
          options.billing.entitlement.active ? 'Studio plan active.' : 'Manage your KilnCat plan.',
        ]),
        billingAction,
      ]),
      el('footer', { class: 'account-footer' }, [
        el(
          'button',
          {
            class: 'library-text-button danger',
            onclick: () => {
              close();
              options.onSignOut();
            },
          },
          ['Sign out'],
        ),
      ]),
    );
  };

  render();
  document.body.appendChild(overlay);
  return close;
}
